import { Extension, type CommandProps } from '@tiptap/core';
import type { ResolvedPos } from '@tiptap/pm/model';

/** Block node types that participate in indentation. */
const INDENTABLE_TYPES = ['paragraph', 'heading'];

/** Node types whose own Tab handlers should win instead of block indent. */
const DEFER_WITHIN = ['listItem', 'taskItem', 'tableCell', 'tableHeader'];

export interface IndentOptions {
  types: string[];
  /** How far each indent level shifts the block, in rem. */
  stepRem: number;
  minLevel: number;
  maxLevel: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      /** Increase the indent level of every selected indentable block. */
      indent: () => ReturnType;
      /** Decrease the indent level of every selected indentable block. */
      outdent: () => ReturnType;
      /** Reset every selected indentable block back to the base margin. */
      clearIndent: () => ReturnType;
    };
  }
}

const asLevel = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** True when the selection sits inside any of the given ancestor node types. */
function isWithin($pos: ResolvedPos, names: string[]): boolean {
  for (let depth = $pos.depth; depth > 0; depth--) {
    if (names.includes($pos.node(depth).type.name)) {
      return true;
    }
  }
  return false;
}

/** Indent level of the nearest indentable ancestor block (0 if none). */
function currentIndentLevel($pos: ResolvedPos, types: string[]): number {
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (types.includes(node.type.name)) {
      return asLevel(node.attrs['indent']);
    }
  }
  return 0;
}

/**
 * Google-Docs / Word style block indentation.
 *
 * Stores an `indent` level on the block node and renders it as `margin-left`,
 * so the whole block shifts and wrapped lines stay aligned to the indented
 * margin (rather than a tab character, which only shifts the first line).
 *
 * Runs at a low priority so the list (sink/lift) and table (next/previous cell)
 * Tab handlers win first; this only indents plain blocks where nothing else
 * handles Tab. Indent is entered with Tab and left with Shift-Tab (Enter is
 * intentionally left alone, so a new line keeps the previous indent until
 * Shift-Tab outdents it).
 */
export const Indent = Extension.create<IndentOptions>({
  name: 'indent',
  priority: 10,

  addOptions() {
    return {
      types: INDENTABLE_TYPES,
      stepRem: 2.5,
      minLevel: 0,
      maxLevel: 10,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) =>
              asLevel(Number(element.getAttribute('data-indent'))),
            renderHTML: (attributes) => {
              const level = asLevel(attributes['indent']);
              if (level <= 0) {
                return {};
              }
              return {
                'data-indent': level,
                style: `margin-left: ${level * this.options.stepRem}rem`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    // Applies `compute` to every selected indentable block's indent level,
    // clamped to [minLevel, maxLevel].
    const applyLevel =
      (compute: (current: number) => number) =>
      () =>
      ({ tr, state, dispatch }: CommandProps): boolean => {
        const { from, to } = state.selection;
        const { types, minLevel, maxLevel } = this.options;
        let changed = false;

        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!types.includes(node.type.name)) {
            return;
          }
          const current = asLevel(node.attrs['indent']);
          const next = Math.min(Math.max(compute(current), minLevel), maxLevel);
          if (next !== current) {
            changed = true;
            if (dispatch) {
              tr.setNodeAttribute(pos, 'indent', next);
            }
          }
        });

        return changed;
      };

    return {
      indent: applyLevel((current) => current + 1),
      outdent: applyLevel((current) => current - 1),
      clearIndent: applyLevel(() => this.options.minLevel),
    };
  },

  addKeyboardShortcuts() {
    return {
      // Consume Tab so it indents instead of moving focus out of the editor,
      // unless a list/table/menu wants it (handled at a higher priority).
      Tab: () => {
        const { $from } = this.editor.state.selection;
        if (isWithin($from, DEFER_WITHIN)) {
          return false;
        }
        this.editor.commands.indent();
        return true;
      },
      // Let Shift-Tab fall through when there's nothing to outdent, so keyboard
      // users can still tab backwards out of the editor.
      'Shift-Tab': () => {
        const { $from } = this.editor.state.selection;
        if (isWithin($from, DEFER_WITHIN)) {
          return false;
        }
        return this.editor.commands.outdent();
      },
    };
  },
});

/**
 * Lets Backspace at the very start of an indented line clear its indent back to
 * the base margin (Word / Google-Docs behavior) instead of merging the line into
 * the previous block.
 *
 * Runs above the core Backspace keymap (priority 100) so it acts before
 * `joinBackward`, but the guard is deliberately narrow: it only fires for a
 * collapsed cursor at the start (`parentOffset === 0`) of an already-indented
 * plain block that isn't inside a list or table. Every other Backspace — mid
 * text, selection deletes, joins, or while a slash/mention menu is open (where
 * the cursor is never at offset 0) — falls through untouched.
 */
export const IndentBackspace = Extension.create({
  name: 'indentBackspace',
  priority: 200,

  addKeyboardShortcuts() {
    return {
      Backspace: () => {
        const { $from, empty } = this.editor.state.selection;
        if (!empty || $from.parentOffset !== 0) {
          return false;
        }
        if (isWithin($from, DEFER_WITHIN)) {
          return false;
        }
        if (currentIndentLevel($from, INDENTABLE_TYPES) <= 0) {
          return false;
        }
        return this.editor.commands.clearIndent();
      },
    };
  },
});
